import { useState, useCallback } from 'react';
import api from '../api/axios';

export const useSync = () => {
  const [syncStatus, setSyncStatus] = useState({
    isInitialized: false,
    isConnected: false,
    isSyncing: false,
    lastSync: null,
    error: null
  });

  const initializeSync = useCallback(async (stagingUri, productionUri) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      
      const response = await api.post('/sync/initialize', {
        stagingUri,
        productionUri
      });
      
      setSyncStatus(prev => ({
        ...prev,
        isInitialized: true,
        isConnected: true,
        isSyncing: false,
        error: null
      }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to initialize sync'
      }));
      throw error;
    }
  }, []);

  const compareData = useCallback(async (scope = {}) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));

      // Scope the comparison to the targeted company/project so the
      // "Data to be Synced" preview reflects only what will actually sync.
      const params = {};
      if (Array.isArray(scope.companyIds) && scope.companyIds.length > 0) {
        params.companyIds = scope.companyIds;
      }
      if (Array.isArray(scope.projectIds) && scope.projectIds.length > 0) {
        params.projectIds = scope.projectIds;
      }

      // Express 5's default "simple" query parser does not understand
      // axios's default `?projectIds[]=a&projectIds[]=b` bracket syntax.
      // Serialize as repeated keys so the backend receives a real array
      // for the Mongoose `$in` filter on companyIds / projectIds.
      const response = await api.get('/sync/compare', {
        params,
        paramsSerializer: (queryParams) => {
          const search = new URLSearchParams();
          Object.entries(queryParams).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach((v) => search.append(key, v));
            } else if (value !== undefined && value !== null) {
              search.append(key, value);
            }
          });
          return search.toString();
        }
      });
      
      setSyncStatus(prev => ({ ...prev, isSyncing: false }));
      
      return response.data.comparison || response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to compare data'
      }));
      throw error;
    }
  }, []);

  const syncCompany = useCallback(async (companyId, options = {}) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      
      const response = await api.post('/sync/companies', {
        companyIds: [companyId], // Send as array to match backend expectation
        ...options
      });
      
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date().toISOString(),
        error: null
      }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to sync company'
      }));
      throw error;
    }
  }, []);

  const syncProject = useCallback(async (projectId, options = {}) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      const { syncExcelTemplates, ...requestOptions } = options;
      
      const response = await api.post('/sync/projects', {
        projectIds: [projectId], // Send as array to match backend expectation
        includeExcelTemplates: syncExcelTemplates !== false,
        ...requestOptions
      });
      
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date().toISOString(),
        error: null
      }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to sync project'
      }));
      throw error;
    }
  }, []);

  const syncFlowSteps = useCallback(async (projectId, options = {}) => {
    try {
      setSyncStatus(prev => ({ ...prev, isSyncing: true, error: null }));
      
      const response = await api.post('/sync/flowsteps', {
        projectIds: [projectId], // Send as array to match backend expectation
        ...options
      });
      
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        lastSync: new Date().toISOString(),
        error: null
      }));
      
      return response.data;
    } catch (error) {
      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        error: error.response?.data?.message || 'Failed to sync flow steps'
      }));
      throw error;
    }
  }, []);

  const closeSync = useCallback(async () => {
    try {
      await api.post('/sync/close');
      
      setSyncStatus({
        isInitialized: false,
        isConnected: false,
        isSyncing: false,
        lastSync: null,
        error: null
      });
    } catch (error) {
      console.error('Failed to close sync connections:', error);
    }
  }, []);

  const getSyncLogs = useCallback(async (filters = {}) => {
    try {
      const response = await api.get('/sync-logs', { params: filters });
      return response.data;
    } catch (error) {
      throw error;
    }
  }, []);

  return {
    syncStatus,
    initializeSync,
    compareData,
    syncCompany,
    syncProject,
    syncFlowSteps,
    closeSync,
    getSyncLogs
  };
};
